import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { Base_URL } from "../../url";
import { getCompanyByOrganisationId } from "../../Api/IncomingPo";
import "../../Styling/generalLedger.css";

import { FaFileExcel, FaFilePdf, FaFilter } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AssetLedger = () => {
  const { user, token } = useAuth();

  // 🔹 Ledger state
  const [ledgerRows, setLedgerRows] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);

  // 🔹 Dropdowns
  const [companyList, setCompanyList] = useState([]);
  const [assetList, setAssetList] = useState([]);

  // 🔹 Filters
  const [orgId, setOrgId] = useState(user?.organisationId || "");
  const [companyCode, setCompanyCode] = useState("");
  const [assetId, setAssetId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [loading, setLoading] = useState(false);

  // 🔹 Load Companies
  useEffect(() => {
    if (!user?.organisationId) return;

    getCompanyByOrganisationId(user.organisationId)
      .then((res) => setCompanyList(res || []))
      .catch(console.error);
  }, [user?.organisationId]);

  // 🔹 Load Assets
  useEffect(() => {
    if (!orgId) return;

    axios
      .get(`${Base_URL}/AllLeger/assets-by-org`, {
        params: { orgId },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => setAssetList(res.data || []))
      .catch((err) => console.error("Asset dropdown error", err));
  }, [orgId, token]);

  // 🔹 Fetch Asset Ledger
  const fetchLedger = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `${Base_URL}/AllLeger/asset-ledger`,
        {
          params: {
            orgId,
            companyCode: companyCode || null,
            assetId: assetId || null,
            month: fromDate ? new Date(fromDate).getMonth() + 1 : null,
            year: fromDate ? new Date(fromDate).getFullYear() : null,
            fromDate: fromDate || null,
            toDate: toDate || null
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setLedgerRows(res.data.ledgerRows || []);
      setOpeningBalance(res.data.openingBalance || 0);
      setClosingBalance(res.data.closingBalance || 0);
      setAvailableBalance(res.data.availableBalance || 0);
      setTotalDebit(res.data.totalDebit || 0);
      setTotalCredit(res.data.totalCredit || 0);
    } catch (err) {
      console.error("Asset Ledger fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Table Rows (Opening + TX + Closing)
  const tableRows = useMemo(() => {
    if (ledgerRows.length === 0) return [];

    const rows = [];

    rows.push({
      type: "OPENING",
      date: fromDate || ledgerRows[0]?.date,
      closingBalance: openingBalance
    });

    ledgerRows.forEach((r) => rows.push({ ...r, type: "TX" }));

    rows.push({
      type: "CLOSING",
      date: toDate || ledgerRows[ledgerRows.length - 1]?.date,
      closingBalance: closingBalance
    });

    return rows;
  }, [ledgerRows, openingBalance, closingBalance, fromDate, toDate]);

  // 🔹 Excel Export
  const downloadExcel = () => {
    const data = tableRows.map((r) => ({
      Date: r.date?.split("T")[0],
      DocumentNo: r.type === "TX" ? r.documentNo : "",
      ItemRefNo: r.type === "TX" ? r.itemRefNo : "",
      Description:
        r.type === "OPENING"
          ? "Opening Balance"
          : r.type === "CLOSING"
          ? "Closing Balance"
          : r.transactionDescription,
      Debit: r.debit || "",
      Credit: r.credit || "",
      Balance: r.closingBalance || r.balance || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AssetLedger");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), "Asset_Ledger.xlsx");
  };

  // 🔹 PDF Export
  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    doc.setFontSize(16);
    doc.text("Asset Ledger", 14, 15);

    doc.setFontSize(10);
    doc.text(`Date Range: ${fromDate || ""} to ${toDate || ""}`, 14, 25);

    const tableData = tableRows.map((r) => [
      r.date ? new Date(r.date).toISOString().split("T")[0] : "",
      r.type === "TX" ? r.documentNo || "" : "",
      r.type === "TX" ? r.itemRefNo || "" : "",
      r.type === "OPENING"
        ? "Opening Balance"
        : r.type === "CLOSING"
        ? "Closing Balance"
        : r.transactionDescription || "",
      r.debit ? Number(r.debit).toFixed(2) : "",
      r.credit ? Number(r.credit).toFixed(2) : "",
      Number(r.closingBalance || r.balance || 0).toFixed(2)
    ]);

    doc.autoTable({
      startY: 30,
      head: [["Date", "Doc No", "Item Ref", "Description", "Debit", "Credit", "Balance"]],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 8 }
    });

    doc.save("Asset_Ledger.pdf");
  };

  return (
    <div className="general-ledger-container">
      <h2>Asset Ledger</h2>

      {/* 🔹 Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <FaFilter /> Filters
        </div>

        <div className="ledger-filters">
          <input
            type="number"
            placeholder="Organisation ID"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="filter-input"
          />

          <select
            value={companyCode}
            onChange={(e) => setCompanyCode(e.target.value)}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companyList.map((c, i) => (
              <option key={i} value={c.companyCode}>
                {c.name} - {c.companyCode}
              </option>
            ))}
          </select>

          <select
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            className="filter-select"
          >
            <option value="">Select Asset</option>
            {assetList.map((a, i) => (
              <option key={i} value={a.assetId}>
                {a.assetName} - {a.assetId}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="filter-input"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="filter-input"
          />

          <button className="btn btn-primary" onClick={fetchLedger}>
            Apply Filter
          </button>
        </div>
      </div>

      {/* 🔹 Actions */}
      <div className="header-actions">
        <button onClick={downloadExcel}><FaFileExcel /> Excel</button>
        <button onClick={downloadPDF}><FaFilePdf /> PDF</button>
      </div>

      {/* 🔹 Table */}
      <table className="ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Doc No</th>
            <th>Item Ref</th>
            <th>Description</th>
            <th>Debit</th>
            <th>Credit</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="7">Loading...</td></tr>
          ) : tableRows.length === 0 ? (
            <tr><td colSpan="7">No data</td></tr>
          ) : (
            tableRows.map((r, i) => (
              <tr key={i}>
                <td>{r.date?.split("T")[0]}</td>
                <td>{r.type === "TX" ? r.documentNo : ""}</td>
                <td>{r.type === "TX" ? r.itemRefNo : ""}</td>
                <td>
                  {r.type === "OPENING"
                    ? "Opening Balance"
                    : r.type === "CLOSING"
                    ? "Closing Balance"
                    : r.transactionDescription}
                </td>
                <td>{r.debit || ""}</td>
                <td>{r.credit || ""}</td>
                <td>{Number(r.closingBalance || r.balance || 0).toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AssetLedger;
